import { Filter } from './filter'
import {
  QueryClient,
  QueryClientProvider,
  useSuspenseQuery,
} from '@w/react-query'
import {
  bindCreateRouteWithContext,
  createMemoryNavigation,
  createRouter,
  createRouterReactBindings,
} from '@w/react-router'
import { s } from '@w/schema'
import { LoadingIndicator } from '@w/ui/components/loading-indicator'
import { ErrorDetails } from '@w/ui/components/error-details'
import { Badge } from '@w/ui/components/badge'
import {
  mergeDDSketchStates,
  percentilesFromDDSketchState,
  type Exemplar,
  type LogData,
  type QuantileSketchData,
  type TraceData,
} from '@w/tel'
import { Button } from '@w/ui/components/button'
import { Card, CardContent } from '@w/ui/components/card'
import { Dialog, DialogContent, DialogTitle } from '@w/ui/components/dialog'
import { IconButton } from '@w/ui/components/icon-button'
import { Input } from '@w/ui/components/input'
import {
  CircleCheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  CopyIcon,
  DownloadIcon,
  SearchIcon,
  TriangleAlertIcon,
  XCircleIcon,
  XIcon,
} from '@w/ui/components/icon'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@w/ui/components/table'
import { Tabs, TabsList, TabsTab } from '@w/ui/components/tabs'
import { ToggleGroup, ToggleGroupItem } from '@w/ui/components/toggle-group'
import { JsonViewer } from '@w/ui/dev/json-viewer'
import { JsonViewer as JsonTreeViewer } from '@w/ui/components/json-viewer'
import traceStyles from './trace-viewer.module.css'
import { traceTimeline } from './trace-timeline'
import {
  createContext,
  useEffect,
  useContext,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'
import {
  createTelReadDb,
  timeFilterMs,
  timeFilters,
  type ErrorGroupRecord,
  type MetricRecord,
  type RemoteTraceRecord,
  type SpanRecord,
  type SpanTableFilters,
  type TelReadDb,
  type TimeFilter,
} from './tel-read-db'
import { subscribeTelDb } from './idb'
import type { TelLink } from './tel-hash-link'
import { cn } from '@w/ui/lib/utils'

interface TelRouterContext {
  telDb: TelReadDb
  queryClient: QueryClient
}
const { createRootRoute, createRoute } =
  bindCreateRouteWithContext<TelRouterContext>()
function TelemetryLoading() {
  return <div className="flex flex-1 items-center justify-center p-4"><LoadingIndicator data-testid="telemetry-loading" /></div>
}
function TelemetryError({ error }: { error: Error }) {
  return <div className="p-4"><ErrorDetails error={error} compact /></div>
}
const rootRoute = createRootRoute({ errorComponent: TelemetryError })
const issuesRoute = createRoute({
  getParentRoute: () => rootRoute,
  pendingComponent: TelemetryLoading,
  path: '/',
  searchSchema: s.object({ status: s.string().optional() }),
  loader: async ({ ctx }) => {
    const { options: issuesOptions } = await ctx.queryClient.load(
      ctx.telDb.errorGroups.listOptions(),
    )
    return { issuesOptions }
  },
  component: IssuesRoute,
})
const issueRoute = createRoute({
  getParentRoute: () => rootRoute,
  pendingComponent: TelemetryLoading,
  path: '/issue/$id',
  loader: async ({ ctx, params }) => {
    const { options: issuesOptions } = await ctx.queryClient.load(ctx.telDb.errorGroups.listOptions())
    return { issuesOptions, id: params.id }
  },
  component: IssueRoute,
})
const spansRoute = createRoute({
  getParentRoute: () => rootRoute,
  pendingComponent: TelemetryLoading,
  path: '/spans',
  searchSchema: s.object({
    target: s.string().optional(),
    status: s.string().optional(),
    resourceName: s.string().optional(),
    timeFrom: s.string().optional(),
    q: s.string().optional(),
    sort: s.string().optional(),
  }),
  loader: async ({ ctx, search }) => {
    const { options: spansOptions } = await ctx.queryClient.load(
      ctx.telDb.spans.listOptions(search as SpanTableFilters),
    )
    const { options: spanFilterOptions } = await ctx.queryClient.load(
      ctx.telDb.spans.listOptions({}),
    )
    return { spansOptions, spanFilterOptions }
  },
  component: SpanTableRoute,
})
const metricsRoute = createRoute({
  getParentRoute: () => rootRoute,
  pendingComponent: TelemetryLoading,
  path: '/metrics',
  searchSchema: s.object({
    metricName: s.string().optional(),
    resourceName: s.string().optional(),
    timeFrom: s.string().optional(),
  }),
  loader: async ({ ctx, search }) => {
    const timeFrom = (search.timeFrom as TimeFilter | undefined) ?? '1h'
    const { options: metricsOptions } = await ctx.queryClient.load(
      ctx.telDb.metrics.listOptions({
        fromMs: Date.now() - timeFilterMs(timeFrom),
      }),
    )
    return { metricsOptions }
  },
  component: MetricsRoute,
})
const apmRoute = createRoute({
  getParentRoute: () => rootRoute,
  pendingComponent: TelemetryLoading,
  path: '/apm',
  loader: async ({ ctx }) => {
    const { options: metricsOptions } = await ctx.queryClient.load(
      ctx.telDb.metrics.listOptions({
        fromMs: Date.now() - timeFilterMs('1h'),
      }),
    )
    return { metricsOptions }
  },
  component: ApmRoute,
})
const traceRoute = createRoute({
  getParentRoute: () => rootRoute,
  pendingComponent: TelemetryLoading,
  path: '/trace/$id',
  loader: async ({ ctx, params }) => {
    const { data: trace, options: traceOptions } = await ctx.queryClient.load(
      ctx.telDb.traces.getOptions(params.id),
    )
    const traceId =
      (trace as { traceId?: string; id?: string } | null)?.traceId ??
      (trace as { traceId?: string; id?: string } | null)?.id ??
      params.id
    const { options: remoteTraceOptions } = await ctx.queryClient.load(
      ctx.telDb.traces.remoteOptions(traceId),
    )
    return { traceOptions, remoteTraceOptions, id: params.id }
  },
  component: TraceRoute,
})
const routeTree = rootRoute.addChildren([
  issuesRoute,
  issueRoute,
  apmRoute,
  metricsRoute,
  spansRoute,
  traceRoute,
])
function _createTelRouter(ctx: TelRouterContext, initialPath = '/spans') {
  return createRouter({
    routeTree,
    ctx,
    navigation: createMemoryNavigation(initialPath),
  })
}
type TelRouter = ReturnType<typeof _createTelRouter>
const { RouterProvider, useLoaderData, useNavigate, useSearch, useRouter } =
  createRouterReactBindings<TelRouter>()
const ExplorerBackContext = createContext<(() => void) | undefined>(undefined)
const RemoteTraceContext = createContext(false)
const StorageLabelContext = createContext<string | undefined>(undefined)

export function TelUi(props: {
  open: boolean
  onClose: () => void
  dbName?: string
  initialPath?: string
}): ReactElement {
  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) props.onClose()
      }}
    >
      <DialogContent
        aria-label="Telemetry explorer"
        className="h-dvh max-h-dvh w-screen min-w-0 max-w-none overflow-hidden rounded-none"
      >
        <DialogTitle className="sr-only">Telemetry explorer</DialogTitle>
        <TelemetryExplorer
          dbName={props.dbName}
          initialPath={props.initialPath}
          onBack={props.onClose}
        />
      </DialogContent>
    </Dialog>
  )
}

/** A selectable telemetry storage; remote stores currently expose traces and their logs. */
export interface TelemetryStorage {
  id: string
  label: string
  dbName?: string
  traceSource?: Pick<TelReadDb, 'spans' | 'traces'>
}

interface TelemetryExplorerProps {
  dbName?: string
  initialPath?: string
  onBack?: () => void
  traceSource?: Pick<TelReadDb, 'spans' | 'traces'>
  storages?: readonly TelemetryStorage[]
  /** Controlled link (`useTelHashLink`): opens its storage and path; reports every navigation. */
  link?: TelLink
}

export function TelemetryExplorer(props: TelemetryExplorerProps): ReactElement {
  // The link's storage id is kept as requested and resolved against the host's storages on every
  // render: storages granted later (admin role loaded) still open it; unknown ids open the first.
  const [selection, setSelection] = useState(() => {
    const link = _parseTelLink(props.link?.value)
    return { storageId: link?.storageId, path: link?.path ?? props.initialPath }
  })
  const storage = props.storages?.find(value => value.id === selection.storageId) ?? props.storages?.[0]
  const storageButtons = props.storages && props.storages.length > 1
    ? (
      <ToggleGroup
        variant="outline"
        size="lg"
        className="w-full medium:w-fit"
        aria-label="Telemetry storage"
        value={storage ? [storage.id] : []}
        onValueChange={value => {
          if (!value[0]) return
          setSelection({ storageId: value[0], path: undefined })
          props.link?.onChange(_formatTelLink(value[0], '/spans'))
        }}
      >
        {props.storages.map(value => (
          <ToggleGroupItem className="min-w-0 flex-1 first:rounded-s-full last:rounded-e-full medium:flex-none" key={value.id} value={value.id}>
            {value.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    )
    : undefined
  return <TelemetryStorageExplorer
    key={storage?.id}
    dbName={storage?.dbName ?? props.dbName}
    traceSource={storage ? storage.traceSource : props.traceSource}
    initialPath={selection.path}
    onBack={props.onBack}
    storageButtons={storageButtons}
    storageLabel={storage?.label}
    onPathChange={props.link && ((path) => { props.link?.onChange(_formatTelLink(storage?.id, path)) })}
  />
}

function _parseTelLink(value: string | undefined): { storageId?: string; path?: string } | undefined {
  if (!value) return undefined
  const slash = value.indexOf('/')
  const prefix = slash === -1 ? value : value.slice(0, slash)
  return {
    storageId: prefix.endsWith(':') ? prefix.slice(0, -1) : prefix || undefined,
    path: slash === -1 ? undefined : value.slice(slash),
  }
}

function _formatTelLink(storageId: string | undefined, path: string): string {
  return storageId === undefined ? path : `${storageId}:${path}`
}

function TelemetryStorageExplorer(props: Omit<TelemetryExplorerProps, 'storages' | 'link'> & {
  storageButtons?: ReactNode
  storageLabel?: string
  onPathChange?: (path: string) => void
}): ReactElement {
  const [state] = useState(() => {
    const ctx = {
      telDb: { ...createTelReadDb({ dbName: props.dbName }), ...props.traceSource },
      queryClient: new QueryClient(),
    }
    return { ...ctx, router: _createTelRouter(ctx, props.initialPath) }
  })
  useEffect(() => {
    state.telDb.invalidateAll(state.queryClient)
    // Trailing throttle: collapse a burst of writes into one invalidation
    // per 500ms window instead of one per write.
    let scheduled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const invalidate = () => {
      if (scheduled) return
      scheduled = true
      timer = setTimeout(() => {
        scheduled = false
        // Only active queries refetch: a view visited earlier and left
        // mounted-but-inactive refetches through its own route loader
        // when revisited, not on every live tick.
        state.telDb.invalidateAll(state.queryClient, { type: 'active' })
      }, 500)
    }
    const unsubscribe = props.traceSource ? () => {} : subscribeTelDb(props.dbName ?? 'tel', invalidate)
    return () => {
      unsubscribe()
      clearTimeout(timer)
    }
  }, [state, props.dbName, props.traceSource])
  const { onPathChange } = props
  useEffect(() => {
    const navigation = state.router.navigation
    if (!onPathChange || !navigation) return () => {}
    const report = () => {
      const url = new URL(navigation.currentEntry.url)
      onPathChange(`${url.pathname}${url.search}`)
    }
    navigation.addEventListener('currententrychange', report)
    return () => { navigation.removeEventListener('currententrychange', report) }
  }, [state, onPathChange])
  return (
    <StorageLabelContext value={props.storageLabel}>
    <RemoteTraceContext value={Boolean(props.traceSource)}>
    <ExplorerBackContext.Provider value={props.onBack}>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-contain">
        {(props.storageButtons ?? props.traceSource) && (
          <div className="flex flex-wrap items-center gap-2 px-4 pt-4" aria-label="Telemetry storage">
            {props.storageButtons}
            {props.traceSource && (
              <Button variant="text" onClick={() => { void state.queryClient.invalidateQueries() }}>
                Refresh
              </Button>
            )}
          </div>
        )}
        <QueryClientProvider client={state.queryClient}>
          <RouterProvider router={state.router} errorFallback={TelemetryError} />
        </QueryClientProvider>
      </div>
    </ExplorerBackContext.Provider>
    </RemoteTraceContext>
    </StorageLabelContext>
  )
}

function ExplorerHeader(props: { children?: ReactNode; onBack?: () => void }) {
  const closeExplorer = useContext(ExplorerBackContext)
  const onBack = props.onBack ?? closeExplorer
  if (!onBack && !props.children) return null
  return (
    <header className="flex min-w-0 shrink-0 flex-col gap-2 py-4 pl-2">
      <div className="flex items-center gap-2">
        {onBack && (
          <IconButton
            semanticRole="secondary"
            size="sm"
            onClick={onBack}
          >
            {props.onBack ? <ChevronLeftIcon /> : <XIcon />}
            Go back
          </IconButton>
        )}
        {props.children}
      </div>
    </header>
  )
}

function SpanTableRoute() {
  const { spansOptions, spanFilterOptions } = useLoaderData({ from: '/spans' })
  const { data: spans } = useSuspenseQuery(spansOptions)
  const { data: filterSpans } = useSuspenseQuery(spanFilterOptions)
  const filters = useSearch({ from: '/spans' }) as SpanTableFilters
  const navigate = useNavigate()
  const [query, setQuery] = useState(filters.q ?? '')
  const setFilter = (key: keyof SpanTableFilters, value: string) => {
    navigate({
      to: '/spans',
      search: { ...filters, [key]: value === 'all' || value === '' ? undefined : value },
      replace: true,
    })
  }
  const sortHead = (sort: 'time' | 'duration', label: string) => {
    const active = (filters.sort ?? 'time') === sort
    return (
      <TableHead className="select-none" aria-sort={active ? 'descending' : 'none'}>
        <Button
          variant="text"
          size="sm"
          data-testid={`sort-${sort}`}
          className={cn(!active && 'text-on-surface-secondary')}
          onClick={() => {
            setFilter('sort', sort === 'time' ? '' : sort)
          }}
        >
          {label}
        </Button>
      </TableHead>
    )
  }
  return (
    <div className="flex min-w-0 shrink-0 flex-col gap-4 p-4">
      <ExplorerHeader />
      <ExplorerNav active="spans" />
      <Card className="bg-transparent min-w-0 shrink-0 ring-0" data-testid="telemetry-spans">
        <CardContent className="flex min-w-0 flex-col gap-6 px-0">
          <Input
            className="w-full max-w-md"
            type="search"
            aria-label="Search traces"
            placeholder="Search name or exact trace id"
            data-testid="span-search"
            leading={<SearchIcon className="size-4" aria-hidden={true} />}
            value={query}
            onChange={(event) => {
              setQuery(event.currentTarget.value)
              setFilter('q', event.currentTarget.value)
            }}
          />
          <div className="flex flex-wrap gap-4">
            <Filter
              label="Target"
              value={filters.target}
              values={_filterValues(filterSpans.map((span) => span.target))}
              onChange={(value) => {
                setFilter('target', value)
              }}
            />
            <Filter
              label="Status"
              value={filters.status}
              values={['ok', 'warning', 'error']}
              onChange={(value) => {
                setFilter('status', value)
              }}
            />
            <Filter
              label="Resource"
              value={filters.resourceName}
              values={_filterValues(
                filterSpans.map((span) => span.resourceName),
              )}
              onChange={(value) => {
                setFilter('resourceName', value)
              }}
            />
            <Filter
              label="Time"
              value={filters.timeFrom}
              values={[...timeFilters]}
              onChange={(value) => {
                setFilter('timeFrom', value)
              }}
            />
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="select-none">Name</TableHead>
                {sortHead('time', 'Time')}
                {sortHead('duration', 'Duration')}
                <TableHead className="select-none">Resource</TableHead>
                <TableHead className="select-none">Target</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {spans.length > 0 ? (
                spans.map((span) => (
                  <TableRow
                    key={span.id}
                    data-testid={`span-${span.id}`}
                    className="min-h-10 cursor-pointer"
                    onClick={() => {
                      navigate({ to: '/trace/$id', params: { id: span.id } })
                    }}
                  >
                    <TableCell className="font-medium">
                      <SpanName span={span} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <ClockIcon className="size-4 shrink-0" />
                        <span>{_formatTimestamp(span.ingestedAt)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-on-surface-secondary tabular-nums">
                      {span.duration === undefined ? 'unfinished' : `${span.duration}ms`}
                    </TableCell>
                    <TableCell>{span.resourceName ?? '—'}</TableCell>
                    <TableCell>{span.target ?? '—'}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5}>No traces found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}

function IssuesRoute() {
  const { issuesOptions } = useLoaderData({ from: '/' })
  const { data: issues } = useSuspenseQuery(issuesOptions)
  const filters = useSearch({ from: '/' }) as IssueTableFilters
  const navigate = useNavigate()
  const visibleIssues = issues.filter(
    (issue) => !filters.status || issue.status === filters.status,
  )
  return (
    <div className="flex min-w-0 shrink-0 flex-col gap-4 p-4">
      <ExplorerHeader />
      <ExplorerNav active="issues" />
      <Card className="bg-transparent min-w-0 shrink-0 ring-0" data-testid="telemetry-issues">
        <CardContent className="flex min-w-0 flex-col gap-6 px-0">
          <div className="flex flex-wrap gap-4">
            <Filter
              label="Status"
              value={filters.status}
              values={['new', 'ongoing']}
              onChange={(value) => {
                navigate({
                  to: '/',
                  search: { status: value === 'all' ? undefined : value },
                  replace: true,
                })
              }}
            />
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="select-none">Issue</TableHead>
                <TableHead className="select-none">Status</TableHead>
                <TableHead className="select-none">Occurrences</TableHead>
                <TableHead className="select-none">Last seen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleIssues.length > 0 ? (
                visibleIssues
                  .toSorted((left, right) => right.lastSeen - left.lastSeen)
                  .map((issue) => (
                    <TableRow
                      key={issue.id}
                      data-testid={`issue-${issue.id}`}
                      className="cursor-pointer"
                      onClick={() => {
                        navigate({ to: '/issue/$id', params: { id: issue.id } })
                      }}
                    >
                      <TableCell>
                        <div className="flex w-48 flex-col gap-1 medium:w-80">
                          <button
                            type="button"
                            className="min-w-0 cursor-pointer text-left outline-primary outline-offset-2 focus-visible:outline-2"
                            onClick={event => {
                              event.stopPropagation()
                              navigate({ to: '/issue/$id', params: { id: issue.id } })
                            }}
                          >
                            <span className="block truncate font-medium" title={issue.title}>{issue.title}</span>
                          </button>
                          <span className="text-on-surface-secondary truncate text-sm" title={issue.errorMessage}>
                            {issue.errorMessage}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <IssueStatusBadge status={issue.status} />
                      </TableCell>
                      <TableCell>{issue.occurrences}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <ClockIcon className="size-4 shrink-0" />
                          <span>{_formatTimestamp(issue.lastSeen)}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4}>No issues found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}

function IssueRoute() {
  const { issuesOptions, id } = useLoaderData({ from: '/issue/$id' })
  const { data: issues } = useSuspenseQuery(issuesOptions)
  const issue = issues.find(value => value.id === id)
  const router = useRouter()
  const navigate = useNavigate()
  const traceId = issue?.lastSpanId ?? issue?.lastTraceId ?? issue?.firstSpanId ?? issue?.firstTraceId
  return (
    <div className="flex min-w-0 shrink-0 flex-col gap-4 p-4">
      <Button
        variant="text"
        semanticRole="secondary"
        className="self-start"
        onClick={() => {
          if (router.navigation?.canGoBack) void router.navigation.back().finished
          else navigate({ to: '/', replace: true })
        }}
      >
        <ChevronLeftIcon />Back to issues
      </Button>
      {issue ? (
        <Card className="min-w-0 bg-transparent ring-0" data-testid="telemetry-issue-details">
          <CardContent className="flex min-w-0 flex-col gap-6 px-0">
            <div className="flex flex-col gap-2">
              <h1 className="text-xl font-semibold">{issue.title}</h1>
              <p className="break-words">{issue.errorMessage}</p>
              <div><IssueStatusBadge status={issue.status} /></div>
            </div>
            <dl className="grid grid-cols-2 gap-4">
              {Object.entries({
                'Error type': issue.errorType,
                Culprit: issue.culpritFn ?? '—',
                Occurrences: issue.occurrences,
                'Affected users': issue.affectedUsers,
                'Affected companies': issue.affectedCompanies,
                'First seen': _formatTimestamp(issue.firstSeen),
                'Last seen': _formatTimestamp(issue.lastSeen),
              }).map(([label, value]) => (
                <div key={label} className="flex min-w-0 flex-col gap-1">
                  <dt className="text-on-surface-secondary text-sm">{label}</dt>
                  <dd className="break-words">{value}</dd>
                </div>
              ))}
            </dl>
            {traceId ? (
              <Button variant="text" className="self-start" onClick={() => { navigate({ to: '/trace/$id', params: { id: traceId } }) }}>
                Open trace<ChevronRightIcon />
              </Button>
            ) : <p className="text-on-surface-secondary">No retained trace is linked to this issue.</p>}
            <section className="flex min-w-0 flex-col gap-2">
              <h2 className="font-semibold">Error</h2>
              <JsonTreeViewer
                data={issue.error}
                rootName="error"
                label="Issue error"
                defaultExpandDepth={2}
                searchable={false}
                copyable={false}
                showPath={false}
                wrapValues
                maxHeight="none"
                className={traceStyles.valueTree}
              />
            </section>
            <section className="flex min-w-0 flex-col gap-2">
              <h2 className="font-semibold">Evidence</h2>
              <JsonTreeViewer
                data={issue.evidence}
                rootName="evidence"
                label="Issue evidence"
                defaultExpandDepth={2}
                searchable={false}
                copyable={false}
                showPath={false}
                wrapValues
                maxHeight="none"
                className={traceStyles.valueTree}
              />
            </section>
            {issue.trace && (
              <section className="flex min-w-0 flex-col gap-2">
                <h2 className="font-semibold">Captured trace</h2>
                <div className={traceStyles.tree}>
                  <TraceNode data={issue.trace} level={0} errorPath={_errorPath(issue.trace)} timeline={traceTimeline(issue.trace)} />
                </div>
              </section>
            )}
          </CardContent>
        </Card>
      ) : <p>Issue not found</p>}
    </div>
  )
}

function MetricsRoute() {
  const { metricsOptions } = useLoaderData({ from: '/metrics' })
  const { data: metrics } = useSuspenseQuery(metricsOptions)
  const filters = useSearch({ from: '/metrics' }) as MetricTableFilters
  const navigate = useNavigate()
  const visibleMetrics = metrics.filter(
    (metric) =>
      (!filters.metricName || metric.name === filters.metricName) &&
      (!filters.resourceName || metric.resourceName === filters.resourceName),
  )
  const setFilter = (key: keyof MetricTableFilters, value: string) => {
    navigate({
      to: '/metrics',
      search: { ...filters, [key]: value === 'all' ? undefined : value },
      replace: true,
    })
  }
  return (
    <ExplorerPage
      active="metrics"
      filters={
        <div className="flex flex-wrap gap-4">
          <Filter
            label="Metric"
            value={filters.metricName}
            values={_filterValues(metrics.map((metric) => metric.name))}
            onChange={(value) => {
              setFilter('metricName', value)
            }}
          />
          <Filter
            label="Resource"
            value={filters.resourceName}
            values={_filterValues(
              metrics.map((metric) =>
                typeof metric.resourceName === 'string'
                  ? metric.resourceName
                  : undefined,
              ),
            )}
            onChange={(value) => {
              setFilter('resourceName', value)
            }}
          />
          <Filter
            label="Time"
            value={filters.timeFrom}
            values={[...timeFilters]}
            onChange={(value) => {
              setFilter('timeFrom', value)
            }}
          />
        </div>
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="select-none">Name</TableHead>
            <TableHead className="select-none">Kind</TableHead>
            <TableHead className="select-none">Value</TableHead>
            <TableHead className="select-none">Resource</TableHead>
            <TableHead className="select-none">Time</TableHead>
            <TableHead className="select-none">Details</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleMetrics.length > 0 ? (
            visibleMetrics
              .toSorted((left, right) => right.ingestedAt - left.ingestedAt)
              .map((metric) => (
                <TableRow key={metric.id} data-testid={`metric-${metric.id}`}>
                  <TableCell className="font-medium">{metric.name}</TableCell>
                  <TableCell>
                    {metric.kind}
                    {metric.temporality ? ` · ${metric.temporality}` : ''}
                  </TableCell>
                  <TableCell>{_metricValue(metric)}</TableCell>
                  <TableCell>
                    {metric.resourceName} · {metric.resourceEnv}
                  </TableCell>
                  <TableCell className="flex items-center gap-2">
                    <ClockIcon className="inline size-4" />{' '}
                    <span>{_formatTimestamp(metric.ingestedAt)}</span>
                  </TableCell>
                  <TableCell>
                    <details>
                      <summary className="cursor-pointer">Details</summary>
                      <JsonViewer data={_metricPoint(metric)} expandedUntil={2} />
                    </details>
                  </TableCell>
                </TableRow>
              ))
          ) : (
            <TableRow>
              <TableCell colSpan={6}>No metrics found</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </ExplorerPage>
  )
}

function ApmRoute() {
  const { metricsOptions } = useLoaderData({ from: '/apm' })
  const { data: metrics } = useSuspenseQuery(metricsOptions)
  const navigate = useNavigate()
  const operations = _apmOperations(metrics)
  return (
    <ExplorerPage
      active="apm"
      context={<p className="text-on-surface-secondary text-sm">last hour</p>}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="select-none">Operation</TableHead>
            <TableHead className="select-none">Requests</TableHead>
            <TableHead className="select-none">Errors</TableHead>
            <TableHead className="select-none">Error rate</TableHead>
            <TableHead className="select-none">Average</TableHead>
            <TableHead className="select-none">P95</TableHead>
            <TableHead className="select-none">Slow trace</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {operations.length > 0 ? (
            operations.map((operation) => (
              <TableRow key={operation.name}>
                <TableCell className="font-medium">{operation.name}</TableCell>
                <TableCell>{operation.requests}</TableCell>
                <TableCell>{operation.errors}</TableCell>
                <TableCell>
                  {_percent(operation.errors, operation.requests)}
                </TableCell>
                <TableCell>
                  {_milliseconds(operation.sum, operation.samples)}
                </TableCell>
                <TableCell>{_milliseconds(operation.p95, 1)}</TableCell>
                <TableCell>
                  {operation.slowestExemplar ? (
                    <Button
                      variant="text"
                      onClick={() => {
                        navigate({
                          to: '/trace/$id',
                          params: {
                            id:
                              operation.slowestExemplar?.trace.requestId ??
                              operation.slowestExemplar?.trace.traceId ??
                              '',
                          },
                        })
                      }}
                    >
                      {_milliseconds(operation.slowestExemplar.value, 1)}
                    </Button>
                  ) : (
                    '—'
                  )}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={7}>No APM data found</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </ExplorerPage>
  )
}

function ExplorerPage(props: {
  active: 'apm' | 'metrics'
  children: ReactElement
  filters?: ReactElement
  context?: ReactElement
}) {
  return (
    <div className="flex min-w-0 shrink-0 flex-col gap-4 p-4">
      <ExplorerHeader />
      <ExplorerNav active={props.active} />
      <Card className="bg-transparent min-w-0 shrink-0 ring-0">
        <CardContent
          className={cn(
            'min-w-0 px-0',
            (props.filters ?? props.context) && 'flex flex-col gap-6',
          )}
        >
          {props.context}
          {props.filters}
          {props.children}
        </CardContent>
      </Card>
    </div>
  )
}

function ExplorerNav(props: {
  active: 'issues' | 'apm' | 'metrics' | 'spans'
}) {
  const navigate = useNavigate()
  const remoteTraces = useContext(RemoteTraceContext)
  if (remoteTraces) return null
  return (
    <Tabs
      className="min-w-0 shrink-0 overflow-x-auto overflow-y-hidden pb-px [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      value={props.active}
      onValueChange={(value) => {
        if (value === 'issues') navigate({ to: '/' })
        if (value === 'apm') navigate({ to: '/apm' })
        if (value === 'metrics') navigate({ to: '/metrics' })
        if (value === 'spans') navigate({ to: '/spans' })
      }}
    >
      <TabsList className="w-full medium:w-fit" aria-label="Telemetry views">
        <TabsTab className="basis-0 medium:basis-auto medium:grow-0" value="spans">Traces</TabsTab>
        <TabsTab className="basis-0 medium:basis-auto medium:grow-0" value="issues">Issues</TabsTab>
        <TabsTab className="basis-0 medium:basis-auto medium:grow-0" value="metrics">Metrics</TabsTab>
        <TabsTab className="basis-0 medium:basis-auto medium:grow-0" value="apm">APM</TabsTab>
      </TabsList>
    </Tabs>
  )
}

type IssueTableFilters = { status?: ErrorGroupRecord['status'] }
type MetricTableFilters = {
  metricName?: string
  resourceName?: string
  timeFrom?: TimeFilter
}
function _filterValues(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.filter((value): value is string => Boolean(value)))].toSorted()
}

function _metricValue(metric: MetricRecord): string {
  if ('value' in metric) return _display(metric.value)
  if ('histogram' in metric) {
    const { count, sum } = metric.histogram.stats
    const percentiles = percentilesFromDDSketchState(metric.quantileSketch)
    return `count ${count} · sum ${sum} · p50 ${percentiles.p50.toFixed(1)} · p95 ${percentiles.p95.toFixed(1)} · p99 ${percentiles.p99.toFixed(1)}`
  }
  if ('exponentialHistogram' in metric) {
    const { count, sum } = metric.exponentialHistogram.stats
    const percentiles = percentilesFromDDSketchState(metric.quantileSketch)
    return `count ${count} · sum ${sum} · p50 ${percentiles.p50.toFixed(1)} · p95 ${percentiles.p95.toFixed(1)} · p99 ${percentiles.p99.toFixed(1)}`
  }
  return '—'
}

function _metricPoint(metric: MetricRecord) {
  const {
    id: _id,
    name: _name,
    kind: _kind,
    temporality: _temporality,
    resourceName: _resourceName,
    resourceEnv: _resourceEnv,
    ingestedAt: _ingestedAt,
    ...point
  } = metric
  return point
}

function _display(value: unknown): string {
  if (value === undefined || value === null) return '—'
  if (typeof value === 'string') return value
  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  )
    return `${value}`
  return JSON.stringify(value)
}

type ApmOperation = {
  name: string
  requests: number
  errors: number
  samples: number
  sum: number
  p95: number
  sketches: QuantileSketchData[]
  slowestExemplar?: Exemplar
}

function _apmOperations(metrics: MetricRecord[]): ApmOperation[] {
  const operations = new Map<string, ApmOperation>()
  for (const metric of metrics) {
    if (metric.name !== 'requests' && metric.name !== 'request.duration')
      continue
    const attrs = metric.streamMeta.attrs ?? {}
    const name = typeof attrs.endpoint === 'string' ? attrs.endpoint : 'unknown'
    const operation = operations.get(name) ?? {
      name,
      requests: 0,
      errors: 0,
      samples: 0,
      sum: 0,
      p95: 0,
      sketches: [],
    }
    if (metric.name === 'requests' && 'value' in metric) {
      operation.requests += metric.value
      if (attrs.failed === true) operation.errors += metric.value
    } else if ('histogram' in metric) {
      operation.samples += metric.histogram.stats.count
      operation.sum += metric.histogram.stats.sum
      operation.sketches.push(metric.quantileSketch)
      for (const exemplar of metric.histogram.exemplars ?? []) {
        if (
          !operation.slowestExemplar ||
          exemplar.value > operation.slowestExemplar.value
        )
          operation.slowestExemplar = exemplar
      }
    }
    operations.set(name, operation)
  }
  for (const operation of operations.values()) {
    const merged = mergeDDSketchStates(operation.sketches)
    operation.p95 = merged ? percentilesFromDDSketchState(merged).p95 : 0
  }
  return [...operations.values()].toSorted(
    (left, right) => right.requests - left.requests,
  )
}

function _percent(value: number, total: number): string {
  return total === 0 ? '—' : `${((value / total) * 100).toFixed(1)}%`
}

function _milliseconds(sum: number, count: number): string {
  return count === 0 ? '—' : `${(sum / count).toFixed(1)} ms`
}

function _formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp)
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}:${pad(Math.floor(date.getMilliseconds() / 10))}`
}

function SpanName({ span }: { span: SpanRecord }) {
  const status = span.status ?? 'unfinished'
  const Icon = status === 'ok' ? CircleCheckIcon : status === 'warning' ? TriangleAlertIcon : status === 'error' ? XCircleIcon : LoadingIndicator
  return (
    <span
      data-testid={`span-status-${status}`}
      className={cn('flex w-48 items-center gap-2 medium:w-80',
        status === 'ok' && 'text-success',
        status === 'warning' && 'text-warning',
        status === 'error' && 'text-error',
        status === 'unfinished' && 'text-on-surface-secondary')}
      title={span.displayName ?? span.name}
    >
      <Icon className="size-4 shrink-0" aria-hidden={true} />
      <span className="min-w-0 truncate leading-none">{span.displayName ?? span.name}</span>
      <span className="sr-only">{status}</span>
      {span.hasFailedDescendant ? (
        <span data-testid="span-failed-descendant" className="text-error flex shrink-0" title="Failed step inside">
          <XCircleIcon className="size-4" aria-hidden={true} />
          <span className="sr-only">Failed step inside</span>
        </span>
      ) : null}
    </span>
  )
}

function IssueStatusBadge(props: { status: ErrorGroupRecord['status'] }) {
  if (props.status === 'new') {
    return (
      <Badge data-testid="issue-status-new" semanticRole="primary" size="xs">
        <XCircleIcon />
        new
      </Badge>
    )
  }
  return (
    <Badge data-testid="issue-status-ongoing" semanticRole="primary" size="xs">
      <TriangleAlertIcon />
      ongoing
    </Badge>
  )
}


function TraceRoute() {
  const { traceOptions, remoteTraceOptions, id } = useLoaderData({
    from: '/trace/$id',
  })
  const storageLabel = useContext(StorageLabelContext)
  const { data } = useSuspenseQuery(traceOptions)
  const { data: remote } = useSuspenseQuery(remoteTraceOptions)
  const navigate = useNavigate()
  const [copyStatus, setCopyStatus] = useState('Copy for agent')
  const [downloadStatus, setDownloadStatus] = useState('Download trace')
  const downloadUrl = useRef<string | null>(null)
  useEffect(() => () => {
    if (downloadUrl.current) URL.revokeObjectURL(downloadUrl.current)
  }, [])
  function downloadTrace() {
    if (!data) return
    try {
      const blob = new Blob([_traceJson({ trace: data, remoteUrl: remote?.url ?? null }, 2)], { type: 'application/json' })
      if (downloadUrl.current) URL.revokeObjectURL(downloadUrl.current)
      const url = URL.createObjectURL(blob)
      downloadUrl.current = url
      const link = document.createElement('a')
      link.href = url
      link.download = `telemetry-trace-${data.traceId ?? data.id}.json`
      document.body.append(link)
      try { link.click() }
      finally { link.remove() }
      // Keep the URL alive until the browser starts the download; release on next export or unmount.
      setDownloadStatus('Download started')
    } catch (error) {
      setDownloadStatus(`Download failed: ${error instanceof Error ? error.message : String(error)}`)
    }
  }
  const router = useRouter()
  const timeline = data ? traceTimeline(data) : undefined
  return (
    <div className="flex min-w-0 shrink-0 flex-col gap-4 p-4">
      <header className="flex min-w-0 shrink-0 items-center justify-between gap-2 py-4">
        <Button
          variant="text"
          semanticRole="secondary"
          size="sm"
          onClick={() => {
            if (router.navigation?.canGoBack) void router.navigation.back().finished
            else navigate({ to: '/', replace: true })
          }}
        >
          <ChevronLeftIcon />
          Back to traces
        </Button>
        {data && (
          remote ? (
            <Badge data-testid="trace-remote-badge" semanticRole="primary" size="xs">
              Remote
            </Badge>
          ) : (
            <Badge data-testid="trace-remote-badge" semanticRole="secondary" size="xs">
              Local only
            </Badge>
          )
        )}
      </header>
      {data && (
        <div className="flex items-center gap-2">
          <Button
            className="min-w-0 flex-1 medium:flex-none"
            variant="text"
            data-testid="copy-for-agent"
            aria-label={copyStatus}
            title={copyStatus}
            size="sm"
            onClick={() => {
              void (async () => {
                try {
                  const text = _copyForAgentText(data, remote)
                  setCopyStatus('Copied')
                  await navigator.clipboard.writeText(text)
                } catch (error) {
                  setCopyStatus(
                    `Copy failed: ${error instanceof Error ? error.message : String(error)}`,
                  )
                }
              })()
            }}
          >
            <CopyIcon />
            {copyStatus}
          </Button>
          <Button
            className="min-w-0 flex-1 medium:flex-none"
            variant="text"
            data-testid="download-trace"
            aria-label={downloadStatus}
            title={downloadStatus}
            size="sm"
            onClick={downloadTrace}
          >
            <DownloadIcon />
            {downloadStatus}
          </Button>
        </div>
      )}
      {data?.previousId && (
        <Button
          variant="text"
          size="sm"
          aria-label="Previous trace"
          title="Previous trace"
          onClick={() => {
            if (data.previousId)
              navigate({ to: '/trace/$id', params: { id: data.previousId } })
          }}
        >
          <ChevronLeftIcon />
          Previous trace
        </Button>
      )}
      {data ? (
        <>
          <TraceHeader data={data} />
          <div className={traceStyles.tree}>
            {timeline && (
              <div data-testid="trace-ruler" className={cn(traceStyles.ruler, 'hidden medium:flex')}>
                <span>0ms</span>
                <span>{timeline.totalMs}ms</span>
              </div>
            )}
            <TraceNode data={data} level={0} errorPath={_errorPath(data)} timeline={timeline} />
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-1">
          <p>Trace not found</p>
          <p className="text-on-surface-secondary font-mono text-sm">
            {storageLabel ? `${id} is not in ${storageLabel}` : id}
          </p>
        </div>
      )}
    </div>
  )
}

type TraceTimeline = ReturnType<typeof traceTimeline>

function TraceHeader(props: { data: TraceData }) {
  const summary = _traceSummary(props.data)
  const fields = [
    ['trace-id', 'Trace', summary.traceId],
    ['start', 'Started', summary.startMs === undefined ? undefined : (
      <time dateTime={new Date(summary.startMs).toISOString()}>{_formatTimestamp(summary.startMs)}</time>
    )],
    ['duration', 'Duration', summary.durationMs === undefined ? undefined : `${summary.durationMs}ms`],
    ['resource', 'Resource', summary.resourceName],
    ['env', 'Environment', summary.resourceEnv],
    ['version', 'Version', summary.resourceVersion],
  ] as const
  return (
    <dl data-testid="trace-header" className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm medium:grid-cols-3">
      {fields.map(([key, label, value]) => (
        <div key={key} className="flex min-w-0 flex-col gap-1">
          <dt className="text-on-surface-secondary">{label}</dt>
          <dd data-testid={`trace-header-${key}`} className="truncate font-mono" title={typeof value === 'string' ? value : undefined}>
            {value ?? '—'}
          </dd>
        </div>
      ))}
    </dl>
  )
}

function TraceNode(props: {
  data: TraceData
  level: number
  errorPath: string[]
  timeline: TraceTimeline
}) {
  const errored = props.errorPath.at(-1) === props.data.id
  const [expanded, setExpanded] = useState(
    props.errorPath.includes(props.data.id) || props.level < 3,
  )
  const items = [
    ...props.data.logs.map((value) => ({ kind: 'log' as const, value })),
    ...props.data.children.map((value) => ({ kind: 'span' as const, value })),
  ].toSorted((left, right) => left.value.id.localeCompare(right.value.id))
  const fields = (['input', 'output', 'error'] as const).filter(
    key => props.data[key] !== undefined,
  )
  const expandable = fields.length > 0 || items.length > 0
  const name = props.data.displayName ?? props.data.name
  const content = (
    <>
      <span className={traceStyles.disclosure} aria-hidden="true">
        {expandable ? (
          <ChevronRightIcon
            size={14}
            strokeWidth={1.75}
            className={cn(traceStyles.chevron, expanded && traceStyles.open)}
          />
        ) : (
          <span className={traceStyles.dot} />
        )}
      </span>
      <strong
        className={cn(
          traceStyles.name,
          props.data.status === 'error' && 'text-error',
        )}
      >
        {name}
      </strong>
      <span className="text-on-surface-secondary">
        {props.data.duration === undefined ? (
          props.data.status === undefined ? (
            <span data-testid={`${props.data.id}-unfinished`}>unfinished</span>
          ) : (
            ''
          )
        ) : (
          `${props.data.duration}ms`
        )}
      </span>
      {props.timeline && (
        <TimelineCell
          id={props.data.id}
          timeline={props.timeline}
          failed={props.data.status === 'error'}
          unfinished={props.data.duration === undefined && props.data.status === undefined}
        />
      )}
    </>
  )
  return (
    <div>
      {expandable ? (
        <button
          type="button"
          className={traceStyles.row}
          aria-label={`Toggle ${name}`}
          aria-expanded={expanded}
          onClick={() => {
            setExpanded(!expanded)
          }}
        >
          {content}
        </button>
      ) : (
        <div className={traceStyles.row}>{content}</div>
      )}
      {expanded && expandable && (
        <div className={traceStyles.children}>
          {fields.map(key => (
            <div key={key} data-testid={`${props.data.id}-${key}`}>
              <JsonTreeViewer
                data={props.data[key]}
                rootName={key}
                label={`${name} ${key}`}
                defaultExpandDepth={errored ? 2 : 0}
                searchable={false}
                copyable={false}
                showPath={false}
                wrapValues
                maxHeight="none"
                className={traceStyles.valueTree}
              />
            </div>
          ))}
          {items.map((item) =>
            item.kind === 'span' ? (
              <TraceNode
                key={item.value.id}
                data={item.value}
                level={props.level + 1}
                errorPath={props.errorPath}
                timeline={props.timeline}
              />
            ) : (
              <LogRow
                key={item.value.id}
                log={item.value}
                onErrorPath={props.errorPath.includes(props.data.id)}
              />
            ),
          )}
        </div>
      )}
    </div>
  )
}

/** Unknown timing: unfinished spans already say so; finished spans without an end time say "no timing". */
function TimelineCell(props: { id: string; timeline: NonNullable<TraceTimeline>; failed: boolean; unfinished: boolean }) {
  const bar = props.timeline.bar(props.id)
  if (!bar && props.unfinished) return null
  if (!bar) return <span className={cn(traceStyles.timing, 'text-on-surface-secondary')} data-testid={`${props.id}-no-timing`}>no timing</span>
  return (
    <span className={traceStyles.timing}>
      <span data-testid={`${props.id}-start`} className="text-on-surface-secondary medium:hidden">+{bar.startMs}ms</span>
      <span className={cn(traceStyles.lane, 'hidden medium:flex')} aria-hidden="true">
        <span
          data-testid={`${props.id}-bar`}
          className={cn(traceStyles.bar, props.failed ? 'bg-error' : 'bg-primary')}
          style={{ marginLeft: `${bar.offsetPct}%`, width: `${bar.widthPct}%` }}
        />
      </span>
    </span>
  )
}

/** Logs carry no event time; only the local store's write time (`ingestedAt`) is shown, labelled as such. */
function LogRow(props: { log: LogData; onErrorPath: boolean }) {
  const { log } = props
  const failed = log.level === 'error' || log.level === 'fatal' || log.error !== undefined
  const [expanded, setExpanded] = useState(props.onErrorPath && failed)
  const summary = _logBody(log.body)
  const storedAt = (log as LogData & { ingestedAt?: unknown }).ingestedAt
  const record = Object.fromEntries(
    Object.entries({ level: log.level, body: log.body, error: log.error, attributes: log.attributes })
      .filter(([, value]) => value !== undefined),
  )
  return (
    <div>
      <button
        type="button"
        data-testid={`log-${log.id}`}
        className={cn(traceStyles.row, failed && 'text-error')}
        aria-label={`Toggle log ${summary}`}
        aria-expanded={expanded}
        title={summary}
        onClick={() => {
          setExpanded(!expanded)
        }}
      >
        <span className={traceStyles.disclosure} aria-hidden="true">
          <ChevronRightIcon size={14} strokeWidth={1.75} className={cn(traceStyles.chevron, expanded && traceStyles.open)} />
        </span>
        <span className={traceStyles.level}>{log.level}</span>
        <span className={traceStyles.log}>{summary}</span>
      </button>
      {expanded && (
        <div className={traceStyles.children} data-testid={`log-${log.id}-record`}>
          {typeof storedAt === 'number' && (
            <p className="text-on-surface-secondary px-2 py-1">Stored at {_formatTimestamp(storedAt)}</p>
          )}
          <JsonTreeViewer
            data={record}
            rootName="log"
            label={`log ${summary}`}
            // log > error > error > message: an error log opens down to its message.
            defaultExpandDepth={3}
            searchable={false}
            copyable={false}
            showPath={false}
            wrapValues
            maxHeight="none"
            className={traceStyles.valueTree}
          />
        </div>
      )}
    </div>
  )
}

function _traceSummary(data: TraceData) {
  const record = data as TraceData & {
    resourceName?: string
    resourceEnv?: string
    resourceVersion?: string
  }
  return {
    traceId: data.traceId ?? data.id,
    name: data.name,
    startMs: traceTimeline(data)?.startMs,
    durationMs: data.duration,
    resourceName: record.resourceName,
    resourceEnv: record.resourceEnv,
    resourceVersion: record.resourceVersion,
  }
}

function _copyForAgentText(
  data: TraceData,
  remote: RemoteTraceRecord | undefined,
): string {
  const summary = _traceSummary(data)
  const header = `Trace ${summary.traceId} · ${summary.name} · ${summary.resourceName ?? ''} ${summary.resourceEnv ?? ''} ${summary.resourceVersion ?? ''}`.trim()
  const remoteLine = remote
    ? `Remote: ${remote.url}`
    : 'Remote: none'
  return `${header}\n${remoteLine}\n${_traceJson(data)}`
}

function _traceJson(value: unknown, space?: number): string {
  // IndexedDB retains native replication bigint cursors. Decimal strings preserve
  // every bit in operator JSON exports, including values above MAX_SAFE_INTEGER.
  return JSON.stringify(value, (_key, item: unknown) =>
    typeof item === 'bigint' ? item.toString() : item, space)
}

function _errorPath(trace: TraceData, path: string[] = []): string[] {
  const next = [...path, trace.id]
  if (trace.status === 'error' || trace.error !== undefined) return next
  for (const child of trace.children) {
    const found = _errorPath(child, next)
    if (found.length > 0) return found
  }
  return []
}
function _logBody(body: unknown): string {
  return body && typeof body === 'object'
    ? Object.entries(body)
        .map(([key, value]) => `${key}=${String(value)}`)
        .join(' ')
    : String(body)
}
